function evaluateCondition(condition, contextData) {
  const parts = (condition.field || '').split('.');
  let resolved = contextData;
  for (const part of parts) {
    resolved = resolved && typeof resolved === 'object' ? resolved[part] : undefined;
  }
  const { operator = 'equals', value = '' } = condition;
  if (operator === 'exists') return resolved !== undefined && resolved !== null && resolved !== '';
  if (resolved === undefined || resolved === null) return false;
  const s = String(resolved);
  if (operator === 'equals') return s === String(value);
  if (operator === 'not_equals') return s !== String(value);
  if (operator === 'contains') return s.toLowerCase().includes(String(value).toLowerCase());
  return false;
}

export const PROMPT_LIKE_TYPES = new Set(['prompt', 'execution_rules', 'knowledge_management', 'intelligence_guidelines', 'forms']);

export function resolveSlots(content, nodeId, nodes, edges, contextData) {
  const slotEdges = edges.filter((e) => e.source === nodeId && e.data?.edgeKind === 'slot');

  return content.replace(/\{\{([a-zA-Z0-9_]+)\}\}/g, (token, name) => {
    const edge = slotEdges.find((e) => e.data?.slotName === name);
    // No matching slot edge: leave the token for downstream handlers (matches the
    // backend's _resolve_slots, so template markers like {{FORMS}} survive)
    if (!edge) return token;
    const slotNode = nodes.find((n) => n.id === edge.target);
    if (!slotNode) return token;
    const variants = slotNode.data?.variants || [];
    for (const variant of variants) {
      if (variant.condition && evaluateCondition(variant.condition, contextData)) {
        return variant.content || '';
      }
    }
    return slotNode.data?.fallback ?? '';
  });
}

export function traversePromptGraph(nodes, edges, contextData) {
  const promptNodes = Object.fromEntries(
    nodes.filter((n) => PROMPT_LIKE_TYPES.has(n.type)).map((n) => [n.id, n])
  );
  if (!Object.keys(promptNodes).length) return [];

  const targetIds = new Set(
    edges.filter((e) => promptNodes[e.target]).map((e) => e.target)
  );
  const rootCandidates = Object.keys(promptNodes).filter((id) => !targetIds.has(id));
  const rootId = rootCandidates[0] || Object.keys(promptNodes)[0];

  const outgoing = {};
  for (const e of edges) {
    if (promptNodes[e.source] && promptNodes[e.target]) {
      (outgoing[e.source] = outgoing[e.source] || []).push(e);
    }
  }

  const fragments = [];
  const visited = new Set();

  function visit(nodeId, behavior = 'append') {
    if (visited.has(nodeId)) return;
    visited.add(nodeId);
    const node = promptNodes[nodeId];
    if (!node) return;
    const fragment = { content: node.data?.content || '', nodeId, nodeType: node.type, promptType: node.data?.promptType, label: node.data?.label || node.type };
    if (behavior === 'replace' && fragments.length) {
      fragments[fragments.length - 1] = fragment;
    } else {
      fragments.push(fragment);
    }
    const nodeEdges = outgoing[nodeId] || [];
    const unconditional = nodeEdges.filter((e) => !e.data?.condition);
    const conditional = nodeEdges.filter((e) => e.data?.condition);
    // Conditionals first so a replace edge swaps the source node before template nodes are appended
    let matching = conditional.filter((e) => evaluateCondition(e.data.condition, contextData));
    if (matching.some((e) => e.data?.exclusive)) matching = matching.slice(0, 1);
    for (const e of matching) {
      visit(e.target, e.data?.behavior || 'append');
    }
    for (const e of unconditional) {
      visit(e.target, e.data?.behavior || 'append');
    }
  }

  visit(rootId);

  // Second pass: resolve {{name}} slot tokens inline within each fragment's content.
  return fragments
    .filter((f) => f.content)
    .map((f) => ({
      ...f,
      content: resolveSlots(f.content, f.nodeId, nodes, edges, contextData),
    }))
    .filter((f) => f.content);
}
